import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Watch } from "lucide-react";
import { api, apiError, DEVICE_TYPE_LABELS, timeAgo } from "@/lib/api";
import Layout from "@/components/Layout";
import { PairingCodeButton } from "@/components/PairingCode";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

const STATUS_VARIANT = { active: "secondary", pending: "outline", offline: "outline", revoked: "destructive" };

export default function Devices() {
  const [devices, setDevices] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/v1/devices");
      setDevices(data.devices);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const revoke = async (id) => {
    try {
      await api.delete(`/v1/devices/${id}`);
      toast.success("Device disconnected. Its credentials have been revoked.");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="devices-page">
        <div className="flex items-center justify-between">
          <h1 className="font-display text-3xl font-medium tracking-tight">Devices</h1>
          <PairingCodeButton onPaired={load} testId="add-device-button" />
        </div>
        <p className="text-sm text-muted-foreground">
          Devices capture your thoughts and send them securely to MIA. Each device gets its own revocable credential.
        </p>

        {loading ? (
          <p className="text-sm text-muted-foreground" data-testid="devices-loading">Loading devices…</p>
        ) : devices.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-border bg-card p-10 text-center" data-testid="devices-empty-state">
            <Watch className="mx-auto h-8 w-8 text-muted-foreground" />
            <p className="mt-4 font-display text-lg font-medium">Apple Watch</p>
            <Badge variant="outline" className="mt-2" data-testid="devices-watch-not-connected">Not connected</Badge>
            <p className="mx-auto mt-4 max-w-sm text-sm leading-relaxed text-muted-foreground">
              The MIA watchOS app is in development. When it's ready, generate a pairing code here and enter it on your watch.
            </p>
            <div className="mt-6 flex items-center justify-center gap-3">
              <Button variant="outline" size="sm" disabled data-testid="setup-when-available-button">
                <Plus className="mr-2 h-4 w-4" /> Set up when available
              </Button>
              <PairingCodeButton onPaired={load} testId="empty-generate-pairing-code-button" />
            </div>
          </div>
        ) : (
          <ul className="space-y-4" data-testid="devices-list">
            {devices.map((d) => (
              <li key={d.id} className="rounded-2xl border border-border bg-card p-6" data-testid={`device-card-${d.id}`}>
                <div className="flex flex-wrap items-center gap-3">
                  <Watch className="h-5 w-5 text-muted-foreground" />
                  <div>
                    <p className="text-sm font-medium" data-testid={`device-name-${d.id}`}>{d.device_name}</p>
                    <p className="text-xs text-muted-foreground">
                      {DEVICE_TYPE_LABELS[d.device_type] || d.device_type} · Last connected: {timeAgo(d.last_seen_at)}
                    </p>
                  </div>
                  <Badge variant={STATUS_VARIANT[d.status] || "outline"} className="ml-auto" data-testid={`device-status-${d.id}`}>
                    {d.status}
                  </Badge>
                </div>
                <div className="mt-4 flex justify-end">
                  <AlertDialog>
                    <AlertDialogTrigger asChild>
                      <Button variant="outline" size="sm" className="text-destructive" data-testid={`disconnect-device-button-${d.id}`}>
                        Disconnect device
                      </Button>
                    </AlertDialogTrigger>
                    <AlertDialogContent data-testid={`disconnect-dialog-${d.id}`}>
                      <AlertDialogHeader>
                        <AlertDialogTitle>Disconnect {d.device_name}?</AlertDialogTitle>
                        <AlertDialogDescription>
                          This device will immediately lose access to your MIA account. Its credential is revoked permanently.
                          Your captured memories are not deleted.
                        </AlertDialogDescription>
                      </AlertDialogHeader>
                      <AlertDialogFooter>
                        <AlertDialogCancel data-testid={`disconnect-cancel-${d.id}`}>Cancel</AlertDialogCancel>
                        <AlertDialogAction data-testid={`disconnect-confirm-${d.id}`} onClick={() => revoke(d.id)}>
                          Disconnect
                        </AlertDialogAction>
                      </AlertDialogFooter>
                    </AlertDialogContent>
                  </AlertDialog>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Layout>
  );
}
